/**
 * Handles and normalizes various error formats, logs the error, and returns an Error object.
 *
 * @param error - The error to handle. Can be an Error object, array of GraphQL errors, string, or unknown.
 * @param file - The filename or context for logging purposes.
 * @param currentFunction - The function name where the error occurred, used for logging.
 * @returns Error - A normalized Error object with an appropriate message.
 */
export const handleError = (error: unknown, file: string, currentFunction: string): Error => {
  const logger = useAppLogger(file)

  if (!error) {
    logger.error(currentFunction, 'No error provided')
    return new Error('No error provided')
  }

  // Handle GraphQL errors array
  if (Array.isArray(error)) {
    const messages = error.map((err) => err.message || 'Unknown error').join(', ')
    logger.error(currentFunction, messages)
    return new Error(messages)
  }

  // Handle GraphQL response with errors property
  if (typeof error === 'object' && 'errors' in error && Array.isArray(error.errors)) {
    const messages = error.errors.map((err) => err.message || 'Unknown error').join(', ')
    logger.error(currentFunction, messages)
    return new Error(messages)
  }

  if (error instanceof Error) {
    logger.error(currentFunction, error.message)
    return error
  }

  if (typeof error === 'string') {
    logger.error(currentFunction, error)
    return new Error(error)
  }

  if ((error as Error)?.message) {
    logger.error(currentFunction, String((error as Error).message))
    return new Error(String((error as Error).message))
  }

  logger.error(currentFunction, 'An unexpected error occurred')
  return new Error('An unexpected error occurred', { cause: error })
}
